import { useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { 
  ShoppingCart, User, LogOut, Shield, Home, Upload, FileText, Mail, Check, X, Loader2
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useCart } from '../context/CartContext';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '../components/ui/tabs';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LOGO_URL = "https://customer-assets.emergentagent.com/job_91be65d6-9fee-4eda-abde-3fd83cab62ae/artifacts/kxr3j7eu_IMG_7793.png";

export default function AdminTracking() {
  const { user, logout, getAuthHeader } = useAuth();
  const { itemCount } = useCart();
  
  // Manual import state
  const [manualData, setManualData] = useState([{ order_id: '', tracking_number: '', carrier: 'USPS' }]);
  const [importing, setImporting] = useState(false);
  
  // Email parsing state
  const [emailBody, setEmailBody] = useState('');
  const [parsing, setParsing] = useState(false);
  const [parsedResults, setParsedResults] = useState(null);
  const [applying, setApplying] = useState(false);

  const addManualRow = () => {
    setManualData([...manualData, { order_id: '', tracking_number: '', carrier: 'USPS' }]);
  };

  const removeManualRow = (index) => {
    if (manualData.length > 1) {
      setManualData(manualData.filter((_, i) => i !== index));
    }
  };

  const updateManualRow = (index, field, value) => {
    const updated = [...manualData];
    updated[index][field] = value;
    setManualData(updated);
  };

  const handleManualImport = async () => {
    const validData = manualData.filter(d => d.order_id && d.tracking_number);
    if (validData.length === 0) {
      toast.error('Please enter at least one order ID and tracking number');
      return;
    }

    try {
      setImporting(true);
      const response = await axios.post(`${API}/admin/tracking/import`, 
        { tracking_data: validData },
        { headers: getAuthHeader() }
      );
      
      if (response.data.success > 0) {
        toast.success(`${response.data.success} tracking numbers imported!`);
      }
      if (response.data.failed > 0) {
        toast.error(`${response.data.failed} failed: ${response.data.errors.join(', ')}`);
      }
      
      // Clear successful rows
      setManualData([{ order_id: '', tracking_number: '', carrier: 'USPS' }]);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Import failed');
    } finally {
      setImporting(false);
    }
  };

  const handleParseEmail = async () => {
    if (!emailBody.trim()) {
      toast.error('Please paste the Pirate Ship email content');
      return;
    }

    try {
      setParsing(true);
      const response = await axios.post(`${API}/admin/tracking/parse-pirateship`,
        { email_body: emailBody },
        { headers: getAuthHeader() }
      );
      
      setParsedResults(response.data);
      
      if (response.data.found.length > 0) {
        toast.success(`Found ${response.data.found.length} tracking number(s)!`);
      } else {
        toast.error('No tracking numbers found in email');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to parse email');
    } finally {
      setParsing(false);
    }
  };

  const handleApplyParsed = async () => {
    if (!parsedResults?.found?.length) return;
    
    const dataToApply = parsedResults.found
      .filter(r => r.order_id && r.tracking_number)
      .map(r => ({
        order_id: r.order_id,
        tracking_number: r.tracking_number,
        carrier: r.carrier || 'USPS'
      }));

    if (dataToApply.length === 0) {
      toast.error('No valid tracking data to apply');
      return;
    }

    try {
      setApplying(true);
      const response = await axios.post(`${API}/admin/tracking/apply-parsed`,
        dataToApply,
        { headers: getAuthHeader() }
      );
      
      if (response.data.success > 0) {
        toast.success(`${response.data.success} tracking numbers applied!`);
        setParsedResults(null);
        setEmailBody('');
      }
      if (response.data.failed > 0) {
        toast.error(`${response.data.failed} failed`);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to apply tracking');
    } finally {
      setApplying(false);
    }
  };

  const updateParsedOrderId = (index, value) => {
    const updated = { ...parsedResults };
    updated.found[index].order_id = value;
    setParsedResults(updated);
  };

  return (
    <div className="min-h-screen bg-[#050505]">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 border-b border-zinc-800 bg-[#050505]/90 backdrop-blur-sm">
        <div className="max-w-7xl mx-auto px-4 md:px-8 h-16 flex items-center justify-between">
          <Link to="/catalog" className="flex items-center gap-3">
            <img src={LOGO_URL} alt="BLK MRKT" className="h-8 w-auto" />
            <Badge className="bg-tiffany-500 text-black rounded-none border-0 hidden sm:inline-flex">ADMIN</Badge>
          </Link>
          
          <nav className="flex items-center gap-2 sm:gap-4">
            <Link to="/catalog" className="p-2 text-zinc-400 hover:text-tiffany-500 transition-colors">
              <Home className="w-5 h-5" />
            </Link>
            <Link to="/cart" className="relative p-2 text-zinc-400 hover:text-tiffany-500 transition-colors">
              <ShoppingCart className="w-5 h-5" />
              {itemCount > 0 && (
                <span className="absolute -top-1 -right-1 bg-tiffany-500 text-black text-xs font-bold w-5 h-5 flex items-center justify-center">
                  {itemCount}
                </span>
              )}
            </Link>
            <Link to="/dashboard" className="p-2 text-zinc-400 hover:text-tiffany-500 transition-colors">
              <User className="w-5 h-5" />
            </Link>
            <Link to="/admin" className="p-2 text-tiffany-500">
              <Shield className="w-5 h-5" />
            </Link>
            <button onClick={logout} className="p-2 text-zinc-400 hover:text-red-500 transition-colors">
              <LogOut className="w-5 h-5" />
            </button>
          </nav>
        </div>
      </header>

      <main className="pt-24 pb-12 px-4 md:px-8 max-w-4xl mx-auto">
        <div className="flex items-center gap-3 mb-2">
          <Link to="/admin" className="text-zinc-500 hover:text-tiffany-500 font-mono text-sm">
            ← BACK TO ADMIN
          </Link>
        </div>

        <div className="flex items-center gap-3 mb-8">
          <Upload className="w-8 h-8 text-tiffany-500" />
          <h1 className="font-sans text-2xl sm:text-3xl text-white tracking-tighter">
            IMPORT TRACKING
          </h1>
        </div>

        <Tabs defaultValue="manual" className="w-full">
          <TabsList className="w-full bg-zinc-900 border border-zinc-800 rounded-none p-1 mb-6">
            <TabsTrigger 
              value="manual" 
              className="flex-1 rounded-none data-[state=active]:bg-tiffany-500 data-[state=active]:text-black font-mono"
            >
              <FileText className="w-4 h-4 mr-2" />
              MANUAL ENTRY
            </TabsTrigger>
            <TabsTrigger 
              value="email" 
              className="flex-1 rounded-none data-[state=active]:bg-tiffany-500 data-[state=active]:text-black font-mono"
            >
              <Mail className="w-4 h-4 mr-2" />
              PARSE EMAIL
            </TabsTrigger>
          </TabsList>

          {/* Manual Entry Tab */}
          <TabsContent value="manual" className="space-y-4">
            <p className="font-mono text-sm text-zinc-500 mb-4">
              Enter order IDs and tracking numbers manually. You can enter multiple at once.
            </p>

            <div className="space-y-3">
              {manualData.map((row, index) => (
                <div key={index} className="flex gap-2 items-center">
                  <Input
                    placeholder="Order ID (e.g., 59350)"
                    value={row.order_id}
                    onChange={(e) => updateManualRow(index, 'order_id', e.target.value)}
                    className="flex-1 h-10 bg-zinc-900 border-zinc-800 text-white font-mono rounded-none"
                  />
                  <Input
                    placeholder="Tracking Number"
                    value={row.tracking_number}
                    onChange={(e) => updateManualRow(index, 'tracking_number', e.target.value)}
                    className="flex-1 h-10 bg-zinc-900 border-zinc-800 text-white font-mono rounded-none"
                  />
                  <select
                    value={row.carrier}
                    onChange={(e) => updateManualRow(index, 'carrier', e.target.value)}
                    className="h-10 px-3 bg-zinc-900 border border-zinc-800 text-white font-mono rounded-none"
                  >
                    <option value="USPS">USPS</option>
                    <option value="UPS">UPS</option>
                    <option value="FedEx">FedEx</option>
                  </select>
                  {manualData.length > 1 && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => removeManualRow(index)}
                      className="text-zinc-400 hover:text-red-500 h-10 w-10 p-0"
                    >
                      <X className="w-4 h-4" />
                    </Button>
                  )}
                </div>
              ))}
            </div>

            <div className="flex gap-3">
              <Button
                variant="outline"
                onClick={addManualRow}
                className="border-zinc-700 text-zinc-300 hover:bg-zinc-800 rounded-none"
              >
                + ADD ROW
              </Button>
              <Button
                onClick={handleManualImport}
                disabled={importing}
                className="bg-tiffany-500 text-black hover:bg-tiffany-400 rounded-none"
              >
                {importing ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    IMPORTING...
                  </>
                ) : (
                  'IMPORT TRACKING'
                )}
              </Button>
            </div>
          </TabsContent>

          {/* Email Parsing Tab */}
          <TabsContent value="email" className="space-y-4">
            <p className="font-mono text-sm text-zinc-500 mb-4">
              Paste the Pirate Ship shipping confirmation email below. The system will attempt to extract tracking numbers automatically.
            </p>

            <Textarea
              placeholder="Paste Pirate Ship email content here..."
              value={emailBody}
              onChange={(e) => setEmailBody(e.target.value)}
              className="w-full h-48 bg-zinc-900 border-zinc-800 text-white font-mono text-sm rounded-none resize-none"
            />

            <Button
              onClick={handleParseEmail}
              disabled={parsing || !emailBody.trim()}
              className="bg-tiffany-500 text-black hover:bg-tiffany-400 rounded-none"
            >
              {parsing ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  PARSING...
                </>
              ) : (
                'PARSE EMAIL'
              )}
            </Button>

            {/* Parsed Results */}
            {parsedResults && (
              <div className="mt-6 border border-zinc-800 p-4">
                <h3 className="font-sans text-lg text-white mb-4">PARSED RESULTS</h3>
                
                {parsedResults.found.length > 0 ? (
                  <div className="space-y-3">
                    {parsedResults.found.map((result, index) => (
                      <div key={index} className="flex flex-col sm:flex-row gap-2 p-3 bg-zinc-900 border border-zinc-800">
                        <div className="flex-1">
                          <label className="block font-mono text-xs text-zinc-500 mb-1">TRACKING</label>
                          <p className="font-mono text-sm text-tiffany-500">{result.tracking_number}</p>
                          <p className="font-mono text-xs text-zinc-600">{result.carrier}</p>
                        </div>
                        <div className="flex-1">
                          <label className="block font-mono text-xs text-zinc-500 mb-1">ORDER ID</label>
                          <Input
                            placeholder="Enter order ID"
                            value={result.order_id || ''}
                            onChange={(e) => updateParsedOrderId(index, e.target.value)}
                            className="h-8 bg-zinc-800 border-zinc-700 text-white font-mono text-sm rounded-none"
                          />
                          {result.order_found && (
                            <p className="font-mono text-xs text-green-500 mt-1 flex items-center gap-1">
                              <Check className="w-3 h-3" /> {result.customer_name}
                            </p>
                          )}
                        </div>
                      </div>
                    ))}

                    <Button
                      onClick={handleApplyParsed}
                      disabled={applying}
                      className="w-full bg-tiffany-500 text-black hover:bg-tiffany-400 rounded-none mt-4"
                    >
                      {applying ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          APPLYING...
                        </>
                      ) : (
                        'APPLY TRACKING NUMBERS'
                      )}
                    </Button>
                  </div>
                ) : (
                  <p className="font-mono text-sm text-zinc-500">
                    No tracking numbers found. Try pasting the full email content.
                  </p>
                )}

                {parsedResults.errors?.length > 0 && (
                  <div className="mt-4 p-3 bg-red-500/10 border border-red-500/30">
                    <p className="font-mono text-sm text-red-500">
                      {parsedResults.errors.join(', ')}
                    </p>
                  </div>
                )}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
}
